// components/landing/sections/SecurityBanner.tsx
// ---------------------------------------------------------------------------
// Prominent compliance and security section. Clearly states GDPR/HIPAA
// compliance, European Azure deployment, and BAA with Microsoft.
// ---------------------------------------------------------------------------

import { Box, Typography, alpha, useTheme } from "@mui/material";
import {
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { athenaColors } from "../colors";

const BADGES = [
  "GDPR Compliant",
  "HIPAA Compliant",
  "Azure EU Instances",
  "BAA with Microsoft",
];

export default function SecurityBanner() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 4,
        py: { xs: 5, md: 6 },
        background: `linear-gradient(135deg, ${athenaColors.deepNavy} 0%, ${alpha(athenaColors.royalBlue, 0.95)} 100%)`,
        textAlign: "center",
      }}
    >
      <Box sx={{ maxWidth: 800, mx: "auto" }}>
        <ShieldIcon
          sx={{ fontSize: 48, color: athenaColors.orange, mb: 2 }}
        />

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.common.white,
            mb: 2,
            fontSize: { xs: "1.3rem", md: "1.7rem" },
          }}
        >
          Enterprise-Grade Security and Compliance
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: alpha(theme.palette.common.white, 0.85),
            fontSize: { xs: "0.95rem", md: "1.05rem" },
            lineHeight: 1.7,
            maxWidth: 700,
            mx: "auto",
          }}
        >
          Secure <strong>agentic environment</strong> compliant with{" "}
          <strong>GDPR</strong> and <strong>HIPAA</strong>, deployed in
          European Azure instances under a{" "}
          <strong>Business Associate Agreement</strong> with Microsoft. All
          patient data is processed within isolated, encrypted environments
          with full audit trails and role-based access control.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            gap: { xs: 1.5, md: 3 },
            flexWrap: "wrap",
          }}
        >
          {BADGES.map((badge) => (
            <Box
              key={badge}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                background: alpha(theme.palette.common.white, 0.06),
              }}
            >
              <VerifiedIcon
                sx={{ fontSize: 16, color: athenaColors.orange }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.85),
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {badge}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
